import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './en.json';
import it from './it.json';
import es from './es.json';
import { STORAGE_KEYS, readStorageValue, writeStorageValue } from '../utils/storageKeys';

// Supported languages
export const SUPPORTED_LANGUAGES = ['en', 'it', 'es'];
export const DEFAULT_LANGUAGE = 'en';

/**
 * Detect language from URL path (e.g., /en/dashboard or /it/settings)
 */
function getLanguageFromUrl() {
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  if (pathSegments.length > 0 && SUPPORTED_LANGUAGES.includes(pathSegments[0])) {
    return pathSegments[0];
  }
  return null;
}

/**
 * Get the preferred language in order of priority:
 * 1. URL path (e.g., /en/ or /it/)
 * 2. Saved preference in localStorage
 * 3. Browser language
 * 4. Default language (en)
 */
function getPreferredLanguage() {
  // 1. Check URL first
  const urlLang = getLanguageFromUrl();
  if (urlLang) {
    return urlLang;
  }
  
  // 2. Check localStorage
  const savedLang = readStorageValue(window.localStorage, STORAGE_KEYS.language)?.value;
  if (savedLang && SUPPORTED_LANGUAGES.includes(savedLang)) {
    return savedLang;
  }
  
  // 3. Check browser language
  const browserLang = navigator.language.split('-')[0];
  if (SUPPORTED_LANGUAGES.includes(browserLang)) {
    return browserLang;
  }
  
  // 4. Fall back to default
  return DEFAULT_LANGUAGE;
}

const initialLanguage = getPreferredLanguage();

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      it: { translation: it },
      es: { translation: es }
    },
    lng: initialLanguage,
    fallbackLng: DEFAULT_LANGUAGE,
    react: {
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['sub', 'sup', 'br', 'strong', 'em']
    },
    interpolation: {
      escapeValue: false
    },
    // Don't use browser detector, we handle it ourselves via URL
    detection: {
      order: [],
      caches: []
    }
  });

// Save language to localStorage whenever it changes
i18n.on('languageChanged', (lng) => {
  writeStorageValue(window.localStorage, STORAGE_KEYS.language, lng);
  // Update document lang attribute for accessibility
  document.documentElement.lang = lng;
});

// Set initial document language
document.documentElement.lang = initialLanguage;

export default i18n;
